/**
 * Field Manager Component
 *
 * Purpose: Manages field instances with CRUD operations, filtering, and validation.
 *          Fields are instances of field definitions assigned to specific models.
 *
 * Architecture: Table-based interface using MDTable component with modal forms
 *              for create/edit operations. Integrates with metadata service.
 *
 * Dependencies: React, Material-UI, MDTable, MetadataService
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  ListSubheader,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Public as GlobalIcon,
  Business as TenantIcon,
  Storage as FixedIcon,
  DataObject as MetadataIcon,
  Restore as RestoreIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import MDTable from '../../../components/md-table';
import { getScopeChipColor } from '../../../constants/mdm';
import { TableColumn } from '../../../types/table';
import metadataService, {
  Field,
  FieldFormData,
  Model,
  TenantInfo,
} from '../../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../../utils/formatting';
import RevertOverrideDialog from '../../../components/metadata/RevertOverrideDialog';
import { FieldModal } from '../../../components/metadata/modals/FieldModal';
import { useFieldData, useFieldDialogs } from '../../../hooks/useFieldManager';
import {
  getFieldLabel,
  getFieldScopeInfo,
  canRevertField,
  prepareGlobalFieldMock,
  getFieldStatus,
} from '../../../utils/fieldHelpers';
import { RowActionButton, RowActions, StatusChip } from '../../../components/common';

// Component interfaces
interface FieldManagerProps {
  tenantContext: TenantInfo;
  canEdit: boolean;
  userPermissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
  onNavigateToView?: (viewId: string) => void;
  preSelectedModelId?: string | null;
  /** Label of the field definition the user navigated from (for back banner) */
  fromFieldDefLabel?: string | null;
  /** Callback to navigate back to the Field Definitions tab */
  onBackToFieldDefinitions?: () => void;
  /** @deprecated Use onBackToFieldDefinitions instead */
  onModelProcessed?: () => void;
}

/**
 * Field Manager Component
 */
const FieldManager: React.FC<FieldManagerProps> = ({
  tenantContext,
  canEdit,
  userPermissions: _userPermissions,
  onNavigateToView,
  preSelectedModelId,
  fromFieldDefLabel,
  onBackToFieldDefinitions,
  onModelProcessed: _onModelProcessed,
}) => {
  // Filters
  const [filters, setFilters] = useState({
    model_id: preSelectedModelId || '',
    status: '',
    storage_type: '',
  });

  // Hide GLOBAL filter - OFF by default (shows all records including GLOBAL)
  const [hideGlobal, setHideGlobal] = useState(false);

  // Use custom hooks for data and dialog management
  const {
    data,
    models,
    loading,
    error,
    parentFieldDefinition,
    effectiveCanEdit,
    canViewRelatedFields,
    loadData,
    loadModels,
    loadViewNames,
  } = useFieldData(tenantContext, canEdit, filters);

  const {
    modalOpen,
    modalMode,
    selectedField,
    openCreateModal,
    openEditModal,
    closeModal,
    revertDialogOpen,
    revertingField,
    globalField,
    revertLoading,
    openRevertDialog,
    closeRevertDialog,
    setRevertLoading,
    viewsPopoverAnchor,
    viewsPopoverData,
    openViewsPopover,
    closeViewsPopover,
  } = useFieldDialogs();

  // Filter data based on hideGlobal setting
  const filteredData = useMemo(() => {
    if (hideGlobal) {
      return data.filter((item) => item.tenant_id !== 'GLOBAL');
    }
    return data;
  }, [data, hideGlobal]);

  // Create custom toolbar filters
  const customToolbarFilters = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Hide GLOBAL Filter */}
        <FormControlLabel
          control={
            <Checkbox
              checked={hideGlobal}
              onChange={(e) => setHideGlobal(e.target.checked)}
              data-testid="hide-global-checkbox"
            />
          }
          label="Hide GLOBAL"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              color: 'text.secondary',
            },
          }}
        />

        {/* Model Filter */}
        <Autocomplete
          data-testid="model-filter-combobox"
          size="small"
          disablePortal
          options={[
            { model_id: '', name: 'All Models' } as unknown as Model,
            ...models.flatMap((group) => group.options),
          ]}
          groupBy={(option) => {
            // Do not group the "All Models" option
            if ((option as any).model_id === '') return '';
            const group = models.find((g) => g.options.includes(option));
            return group?.group || '';
          }}
          renderGroup={(params) =>
            params.group ? (
              <React.Fragment key={params.key}>
                <ListSubheader
                  sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}
                >
                  {params.group}
                </ListSubheader>
                {params.children}
              </React.Fragment>
            ) : (
              <React.Fragment key={params.key}>{params.children}</React.Fragment>
            )
          }
          getOptionLabel={(option) =>
            (option as any).model_id === '' ? 'All Models' : option.name
          }
          isOptionEqualToValue={(option, value) => option.model_id === value.model_id}
          value={(() => {
            const allOption = { model_id: '', name: 'All Models' } as unknown as Model;
            const all = [allOption, ...models.flatMap((group) => group.options)];
            return all.find((m) => m.model_id === (filters.model_id || '')) || allOption;
          })()}
          onChange={(_event, newValue) => {
            setFilters((prev) => ({ ...prev, model_id: newValue?.model_id || '' }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Model" placeholder="Search models..." size="small" />
          )}
          sx={{ minWidth: 220 }}
          clearOnEscape
        />
      </Box>
    ),
    [hideGlobal, filters.model_id, models]
  );

  // Load data on component mount
  useEffect(() => {
    loadModels();
    loadData();
  }, [loadModels]);

  // Reload data when filters change
  useEffect(() => {
    loadData();
  }, [filters.model_id, filters.status]);

  // Sync filter with URL-driven preSelectedModelId
  useEffect(() => {
    if (preSelectedModelId && models.length > 0) {
      setFilters((prev) => ({ ...prev, model_id: preSelectedModelId }));
    }
  }, [preSelectedModelId, models]);

  // Table columns configuration
  const columns: TableColumn[] = [
    {
      field: 'label', // Virtual field - valueGetter will compute the actual value
      headerName: 'Label',
      width: 200,
      sortable: true,
      valueGetter: (params: any) => {
        // For MUI DataGrid v8, valueGetter receives params object with { value, row, field, ... }
        // Compute label for sorting/filtering: label_override || field_definition_label (NOT fielddef_id)
        if (!params || !params.row) {
          return '';
        }
        const row = params.row as Field;
        return getFieldLabel(row);
      },
      renderCell: (_value: any, row: Field) => {
        return <Typography variant="body2">{getFieldLabel(row)}</Typography>;
      },
    },
    {
      field: 'field_definition_label',
      headerName: 'Field Definition',
      width: 180,
      sortable: true,
      renderCell: (value: string, row: Field) => {
        // Use field_definition_label if available, otherwise try to get from field_definition relationship
        const fieldDefLabel = value || row.field_definition?.label || 'N/A';
        return (
          <Typography variant="body2" color="textSecondary">
            {fieldDefLabel}
          </Typography>
        );
      },
    },
    {
      field: 'tenant_id',
      headerName: 'Scope',
      width: 120,
      sortable: true,
      renderCell: (_value: string, row: Field) => {
        const scopeInfo = getFieldScopeInfo(row);
        const scopeIcon =
          scopeInfo.scope === 'GLOBAL' ? (
            <GlobalIcon fontSize="small" />
          ) : (
            <TenantIcon fontSize="small" />
          );

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {scopeIcon}
            <Chip
              label={scopeInfo.scope}
              size="small"
              color={getScopeChipColor(scopeInfo.scope)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'model_id',
      headerName: 'Model',
      width: 150,
      sortable: true,
      renderCell: (value: string, row: Field) => {
        const modelName = row.model_name || value;
        const modelType = row.model_type;
        const isFieldDef = modelType === 'fielddef';

        const handleModelClick = (event: React.MouseEvent) => {
          event.stopPropagation(); // Prevent event from bubbling up to table row
          setFilters((prev) => ({ ...prev, model_id: value }));
        };

        return (
          <Chip
            label={modelName}
            size="small"
            variant="outlined"
            color={isFieldDef ? 'secondary' : 'primary'}
            onClick={handleModelClick}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isFieldDef ? 'secondary.dark' : 'primary.dark',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        );
      },
    },
    {
      field: 'storage_type',
      headerName: 'Storage',
      width: 100,
      sortable: true,
      renderCell: (value: string) => (
        <Chip
          icon={
            value === 'fixed' ? <FixedIcon fontSize="small" /> : <MetadataIcon fontSize="small" />
          }
          label={value}
          size="small"
          color={value === 'fixed' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },

    {
      field: 'is_required',
      headerName: 'Required',
      width: 80,
      sortable: true,
      renderCell: (value: boolean) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'error' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'description_override',
      headerName: 'Description Override',
      width: 200,
      sortable: true,
      renderCell: (value: string) => (
        <Typography variant="body2" color="textSecondary">
          {value || '—'}
        </Typography>
      ),
    },
    {
      field: 'is_multiselect',
      headerName: 'Multi-select',
      width: 100,
      sortable: true,
      renderCell: (value: boolean) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'storage_decimal_places_override',
      headerName: 'Storage Decimal Places Override',
      width: 220,
      sortable: true,
      renderCell: (value: number) => (
        <Typography variant="body2" color="textSecondary">
          {value !== null && value !== undefined ? value : '—'}
        </Typography>
      ),
    },
    {
      field: 'display_decimal_places_override',
      headerName: 'Display Decimal Places Override',
      width: 220,
      sortable: true,
      renderCell: (value: number) => (
        <Typography variant="body2" color="textSecondary">
          {value !== null && value !== undefined ? value : '—'}
        </Typography>
      ),
    },
    {
      field: 'views',
      headerName: 'Views',
      width: 120,
      sortable: false,
      renderCell: (value: Record<string, number>) => {
        const viewCount = Object.keys(value || {}).length;
        const viewIds = Object.keys(value || {});

        const handleViewsClick = async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          if (viewCount > 0) {
            const viewNames = await loadViewNames(viewIds);
            openViewsPopover(event.currentTarget, viewIds, viewNames);
          }
        };

        const handleViewClick = (viewId: string) => {
          // Close the popover
          closeViewsPopover();

          // Navigate to Views tab with the specific view selected
          if (onNavigateToView) {
            onNavigateToView(viewId);
          }
        };

        return (
          <>
            <Chip
              label={`${viewCount} view${viewCount !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              onClick={handleViewsClick}
              sx={{
                cursor: viewCount > 0 ? 'pointer' : 'default',
                '&:hover': viewCount > 0 ? { backgroundColor: 'action.hover' } : {},
              }}
            />
            <Popover
              open={Boolean(viewsPopoverAnchor)}
              anchorEl={viewsPopoverAnchor}
              onClose={closeViewsPopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: { maxHeight: 300, minWidth: 200 },
                },
              }}
            >
              {viewsPopoverData && (
                <List dense>
                  {viewsPopoverData.viewIds.map((viewId) => (
                    <ListItem key={viewId} disablePadding>
                      <ListItemButton onClick={() => handleViewClick(viewId)}>
                        <ListItemText
                          primary={viewsPopoverData.viewNames[viewId] || viewId}
                          secondary={viewId}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Popover>
          </>
        );
      },
    },
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: Field) => formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'status_override',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (_value: string, row: Field) => {
        const status = getFieldStatus(row);
        return <StatusChip status={status} />;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (_value: any, row: Field) => (
        <RowActions
          entityLabel="Field"
          rowLabel={getFieldLabel(row)}
          testIdPrefix="field"
          onEdit={() => handleEdit(row)}
          secondaryActions={
            canRevertField(row) && (
              <RowActionButton
                label="Revert to Global Field"
                testId="field-revert"
                onClick={() => handleRevertField(row)}
                disabled={!effectiveCanEdit}
                icon={
                  <RestoreIcon fontSize="small" color={effectiveCanEdit ? 'warning' : 'disabled'} />
                }
              />
            )
          }
          deactivation={{
            variant: 'disable',
            isActive: row.status_override !== 'disabled',
            onToggle: () => handleStatusToggle(row),
            disabled: !effectiveCanEdit,
          }}
        />
      ),
    },
  ];

  // Event handlers
  const handleCreate = () => {
    openCreateModal();
  };

  const handleEdit = (field: Field) => {
    openEditModal(field);
  };

  const handleRowClick = (row: Field) => {
    handleEdit(row);
  };

  const handleSave = async (formData: FieldFormData) => {
    if (modalMode === 'create') {
      await metadataService.createField(formData);
    } else if (selectedField) {
      // Backend handles GLOBAL field override logic automatically
      await metadataService.updateField(selectedField.field_id, formData, tenantContext.tenant_id);
    }
    loadData();
  };

  const handleDelete = async (field: Field) => {
    await metadataService.deleteField(field.field_id);
    loadData();
  };

  const handleStatusToggle = async (field: Field) => {
    const newStatus = field.status_override === 'disabled' ? 'active' : 'disabled';
    await metadataService.updateField(field.field_id, {
      ...field,
      status_override: newStatus,
    });
    loadData();
  };

  // Revert handlers
  const handleRevertField = useCallback(
    async (field: Field) => {
      // Fetch the global field definition that will become visible
      try {
        const globalFieldDefinition = await metadataService.getFieldDefinition(
          field.fielddef_id,
          'GLOBAL'
        );

        const mockGlobalField = globalFieldDefinition
          ? prepareGlobalFieldMock(field, globalFieldDefinition)
          : null;

        openRevertDialog(field, mockGlobalField);
      } catch (error) {
        console.error('Failed to fetch global field definition:', error);
        openRevertDialog(field, null);
      }
    },
    [openRevertDialog]
  );

  const handleRevertConfirm = useCallback(async () => {
    if (!revertingField) return;

    setRevertLoading(true);
    try {
      await metadataService.revertField(revertingField.field_id);
      closeRevertDialog();
      // Wait for data to reload before closing loading state
      await loadData();
    } catch (error) {
      console.error('Failed to revert field:', error);
      // Error handling could be enhanced with a snackbar notification
    } finally {
      setRevertLoading(false);
    }
  }, [revertingField, closeRevertDialog, loadData]);

  const handleRevertClose = useCallback(() => {
    closeRevertDialog();
  }, [closeRevertDialog]);

  return (
    <Box>
      {/* Show message for Internal Only field definitions */}
      {!canViewRelatedFields && parentFieldDefinition && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This field definition is internal only and not accessible in your tenant context. Related
          fields cannot be viewed or modified.
        </Alert>
      )}

      {/* Back to Field Definition banner */}
      {fromFieldDefLabel && onBackToFieldDefinitions && (
        <Box
          data-testid="back-to-field-definition-banner"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.selected' },
          }}
          onClick={onBackToFieldDefinitions}
        >
          <ArrowBackIcon fontSize="small" color="primary" />
          <Typography variant="body2" color="primary" fontWeight="medium">
            Back to Field Definitions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            — viewing fields for "{fromFieldDefLabel}"
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      <Box>
        <MDTable
          mode="manual"
          manual={{
            columns,
            title: 'Fields',
            description: `${filteredData.length} field(s) found`,
          }}
          data={filteredData.map((item) => ({ ...item, id: item.field_id }))}
          loading={loading}
          error={error}
          onRowClick={handleRowClick}
          onRefresh={loadData}
          onAddNew={canEdit ? handleCreate : undefined}
          showRefreshButton={true}
          showAddButton={canEdit}
          selectable={false}
          defaultSortModel={[{ field: 'label', sort: 'asc' }]}
          sortable={true}
          filterable={true}
          customToolbarFilters={customToolbarFilters}
          defaultColumnVisibility={{
            description_override: false,
            is_multiselect: false,
            storage_decimal_places_override: false,
            display_decimal_places_override: false,
          }}
        />
      </Box>

      {/* Create/Edit Modal */}
      <FieldModal
        open={modalOpen}
        mode={modalMode}
        field={selectedField}
        tenantContext={tenantContext}
        canEdit={canEdit}
        effectiveCanEdit={effectiveCanEdit}
        parentFieldDefinition={parentFieldDefinition}
        preSelectedModelId={filters.model_id || undefined}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      {/* Revert Dialog */}
      <RevertOverrideDialog
        open={revertDialogOpen}
        onClose={handleRevertClose}
        onConfirm={handleRevertConfirm}
        overrideItem={revertingField}
        globalItem={globalField}
        loading={revertLoading}
        itemType="field"
        relatedFields={[]}
      />
    </Box>
  );
};

export default FieldManager;
